import {
  BackgroundColors,
  NegativeColors,
  PrimaryColors,
  Shadows,
} from '@picsart/design-system/foundation/colors';
import { FontSizes, LineHeights, TypographyT4 } from '@picsart/design-system/foundation/typography';

import { createUseStyles } from 'react-jss';

// import { BreakPointsMedia } from '@picsart/design-system/foundation/responsive';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { FontWeights } from '@picsart/design-system/foundation/fonts';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  canHover,
  isLeftToRight,
  isRightToLeft,
} from '@picsart/design-system/foundation/styleHelpers';

const useStyles = createUseStyles(
  {
    section: {
      minWidth: 0,
      minHeight: 0,
      gridArea: 'aside',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      // replace with [BreakPointsMedia.MD], [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1280px)': {
        width: 'initial',
      },

      [isLeftToRight]: {
        paddingLeft: Spacings.s24,
        // replace with [BreakPointsMedia.MD], [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
        '@media screen and (max-width: 1280px)': {
          border: 'none',
        },
      },
      [isRightToLeft]: {
        borderRight: `1px solid ${BackgroundColors.tint2}`,
        paddingRight: Spacings.s24,
        // replace with [BreakPointsMedia.MD], [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
        '@media screen and (max-width: 1280px)': {
          border: 'none',
          padding: 0,
        },
      },
    },
    sidebar: {
      maxWidth: 372,
      display: 'flex',
      flexDirection: 'column',
      gap: Spacings.s12,
      // replace with [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1024px)': {
        maxWidth: '100%',
        width: '100%',
        marginTop: Spacings.s16,
      },
    },
    topPart: {
      // replace with [BreakPointsMedia.MD], [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1280px)': {
        padding: [0, Spacings.s24],
      },
    },
    t2: {
      marginTop: Spacings.s24,
      textTransform: 'capitalize',
      // replace with [BreakPointsMedia.MD], [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1024px)': {
        marginTop: Spacings.s16,
      },
    },
    t5: {
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': 2,
      overflow: 'hidden',
    },
    tCommon: {
      display: 'flex',
      alignItems: 'center',
      columnGap: Spacings.s8,
      minHeight: 20,
      [canHover]: {
        '&:hover': {
          '& > a': {
            color: NegativeColors.base.default,
          },
          '& > svg > path': {
            fill: NegativeColors.base.default,
          },
        },
      },
      paddingBlock: Spacings.s10,
    },
    tIcon: {
      display: 'inline-block',
      width: 20,
      height: 20,

      '& > path': {
        fill: NegativeColors.base.action,
      },
    },
    t6: {
      marginTop: Spacings.s24,
      // replace with [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1024px)': {
        marginTop: Spacings.s16,
      },
    },
    designDetails: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: Spacings.s32,
      gap: Spacings.s16,
    },
    detailsBlockSize: {
      display: 'flex',
      flexDirection: 'row',
      gap: Spacings.s48,
    },
    detailsBlockLicense: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'start',
      gap: 25,
    },
    license: {
      display: 'flex',
      flexDirection: 'row',
      columnGap: Spacings.s6,
    },
    infoIcon: {
      width: 20,
      height: 20,
      cursor: 'pointer',
    },
    bottomPart: {
      display: 'inline-flex',
      flexDirection: 'column',
      rowGap: Spacings.s24,
      // replace with [BreakPointsMedia.SM], [BreakPointsMedia.XS], [BreakPointsMedia.XXS]{
      '@media screen and (max-width: 1024px)': {
        rowGap: Spacings.s16,
      },
    },
    tooltipText: {
      display: 'inline-block',
      padding: Spacings.s8,
      borderRadius: CornerRadius.r4,
      verticalAlign: 'top',
      fontSize: FontSizes.s14,
      maxWidth: 250,
      wordBreak: 'break-word',
      textAlign: 'left',
    },
    licenseText: {
      textTransform: 'capitalize',
    },
    description: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: Spacings.s16,
    },
    flexContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      fontWeight: FontWeights.semibold,
      fontSize: TypographyT4,
    },
    inlineButton: {
      display: 'inline-flex',
      padding: [Spacings.s6, 0, Spacings.s6, 0],
      gap: Spacings.s4,
    },
    mobileEditAndShare: {
      boxShadow: Shadows.tint3,
      padding: [0, Spacings.s24, Spacings.s12, Spacings.s24],
      position: 'fixed',
      bottom: 0,
      right: 0,
      left: 0,
      zIndex: 1000,
      backgroundColor: PrimaryColors.text.default,
    },
    tLink: {
      color: NegativeColors.base.action,
      fontSize: FontSizes.s14,
      fontWeight: FontWeights.semibold,
      lineHeight: LineHeights.l20,
    },
  },
  { name: 'aside' },
);

export default useStyles;
